import type { NavigationGuardNext, RouteLocationNormalized } from "vue-router";
import FlowParticipationsApi from "@/data/api-layer/flowParticipationsApi";
import FlowsApi from "@/data/api-layer/flowsApi";

export enum FlowAccessErrorCodeStatus {
   STUDY_PARTICIPATION_INACTIVE = "study_participation_inactive", // study participation is inactive (redirect to error page)
   CODE_REQUIRED = "study_invitation_code_required", // study requires an access code (redirect to study access)
   DEVICE_COOKIE_ERROR = "Device cookie not sent or not valid", // device cookie not sent or not valid (redirect to error page)
   NO_TOKEN_SENT = "No token sent for endpoint requiring authentication", // user needs to be logged in to check access (redirect to login page)
   LOCALE_DOES_NOT_MATCH_SET_FLOW_LOCALE = "locale_does_not_match_set_flow_locale", // flow requested in a locale different from existing flow-participation (redirect to flow in existing locale)
   FLOW_LOCALE_NOT_FOUND = "flow_locale_not_found", // flow not found in requested locale (redirect to error page)
   UNAUTHORIZED_FLOW_USER = "unauthorized_flow_user", // not currently eligible to participate in this survey (redirect to error page)
   FLOW_NOT_FOUND = "flow_not_found", // flow not found (redirect to error page)
   USER_NOT_RELEVANT_TO_FLOW = "user_not_relevant_to_flow", // user not relevant for the flow (redirect to home page)
}

interface AccessError extends Error {
   response: {
      data: {
         error_code: FlowAccessErrorCodeStatus; // returned from BE participation access
         error?: FlowAccessErrorCodeStatus; // returned from BE auth middleware
         data?: {
            existing_participation_locale_id?: string; // if error is LOCALE_DOES_NOT_MATCH_SET_FLOW_LOCALE
         };
      };
   };
}

/**
 * All flows routes are protected by this middleware.
 * If the flow is public, it allows access.
 * If not, it checks if the flow needs an access code
 * - If no access is required, allow access
 * - If access is required;
 * -- if the user is not logged in, redirect to the login page (we need the user data for the access check)
 * -- if logged in;
 * --- check if user has access to the flow (has participated in the study), allow access
 * --- if not, redirect to the study access page (to enter code)
 */
export default async function studyFlowAccessMiddleware(
   to: RouteLocationNormalized,
   from: RouteLocationNormalized,
   next: NavigationGuardNext,
   loggedIn: boolean
) {
   let studyKey = null;
   try {
      /*
         we need to get the flow to check if its "isPrivateFlow",
         because the getOrCreateParticipation method will a flow participation (createIfNotFound) and
         we should not that for non "isPrivateFlow" - we'll just allow access and let the flowsel handle that
      */
      const flowResponse = await FlowsApi.getFlowById(to.params.id as string);
      const flow = flowResponse.data.data;
      studyKey = flow?.study?.key;

      // private flows include isPublic=False or (isPublic=True and loggedIn and anonymousSubmissionsOnly=False)
      const isPrivateFlow = !flow.isPublic || (loggedIn && !flow.anonymousSubmissionsOnly);

      if (!isPrivateFlow) {
         console.info("Flow is public, allowing access");
         next();
         return;
      } else {
         await FlowParticipationsApi.getOrCreateParticipation(to.params.id as string);
         next(); // Allow access if no error is thrown
         return;
      }
   } catch (error: any) {
      const errorResponse = (error as AccessError).response?.data;
      const targetFlowUrl = to.fullPath;

      // These might be returned from the BE if the flow is not public and the user is not logged in
      if (
         errorResponse?.error === FlowAccessErrorCodeStatus.NO_TOKEN_SENT ||
         errorResponse?.error === FlowAccessErrorCodeStatus.DEVICE_COOKIE_ERROR
      ) {
         console.warn("Access check requires authentication", error);

         next({ name: "login", params: { locale: to.params?.locale }, query: { redirect: targetFlowUrl } });
         return;
      } else if (errorResponse.error_code === FlowAccessErrorCodeStatus.CODE_REQUIRED) {
         console.warn("Access check requires authentication", error);

         next({
            name: "study-access",
            params: { locale: to.params?.locale, key: studyKey },
         });
         return;
      } else if (errorResponse.error_code === FlowAccessErrorCodeStatus.LOCALE_DOES_NOT_MATCH_SET_FLOW_LOCALE) {
         next({
            name: "flow",
            query: to.query,
            params: { ...to.params, locale: errorResponse.data.existing_participation_locale_id },
         });
         return;
      } else if (errorResponse.error_code === FlowAccessErrorCodeStatus.FLOW_LOCALE_NOT_FOUND) {
         console.error("Flow locale not found: ", to.params.id, to.params?.locale);

         next({
            name: "error-page",
            params: { locale: to.params?.locale },
            query: { error_type: FlowAccessErrorCodeStatus.FLOW_LOCALE_NOT_FOUND },
         });
         return;
      } else if (errorResponse.error_code === FlowAccessErrorCodeStatus.UNAUTHORIZED_FLOW_USER) {
         // not currently eligible to participate in this survey
         console.error("Unauthorized flow user: ", to.params.id);

         next({
            name: "error-page",
            params: { locale: to.params?.locale },
            query: { error_type: FlowAccessErrorCodeStatus.UNAUTHORIZED_FLOW_USER },
         });
         return;
      } else if (errorResponse.error_code === FlowAccessErrorCodeStatus.FLOW_NOT_FOUND) {
         console.error("Flow not found: ", to.params.id);
         next({
            name: "error-page",
            params: { locale: to.params?.locale },
            query: { error_type: FlowAccessErrorCodeStatus.FLOW_NOT_FOUND },
         });
         return;
      } else if (errorResponse.error_code === FlowAccessErrorCodeStatus.USER_NOT_RELEVANT_TO_FLOW) {
         console.warn("Not relevant for flow: ", to.params.id); // should not trigger sentry
         next({ name: "home", params: { locale: to.params?.locale } });
         return;
      } else if (errorResponse.error_code === FlowAccessErrorCodeStatus.STUDY_PARTICIPATION_INACTIVE) {
         console.error("Study participation is inactive: ", to.params.id);

         next({
            name: "error-page",
            params: { locale: to.params?.locale },
            query: { error_type: FlowAccessErrorCodeStatus.STUDY_PARTICIPATION_INACTIVE },
         });
         return;
      }
      // Unexpected error
      else {
         console.error("Unexpected error accessing flow: ", to.params.id);

         next({
            name: `error-page`,
            params: { locale: to.params?.locale },
         });
         return;
      }
   }
}
